import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function OnboardingDialog({ open, onAccepted }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.patch("/users/me", { terms_accepted: true });
      onAccepted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-xl w-[calc(100vw-2rem)] max-h-[90vh] p-0 gap-0 flex flex-col [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="onboarding-dialog"
      >
        {/* Fixed header */}
        <DialogHeader className="p-6 pb-4 border-b border-slate-200 flex-shrink-0 space-y-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 border border-[#0D9488]/40 flex items-center justify-center bg-[#0D9488]/10">
              <AlertTriangle className="w-4 h-4 text-[#0D9488]" strokeWidth={2.2} />
            </div>
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">
              // Educational Aid
            </div>
          </div>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl md:text-3xl font-black">
            Before you begin
          </DialogTitle>
          <DialogDescription className="sr-only">
            Educational aid disclaimer for The Amino Exchange. Review the terms and acknowledge before continuing.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="p-6 space-y-3 text-sm text-slate-800 leading-relaxed overflow-y-auto flex-1 min-h-0">
          <p>
            <span className="font-semibold text-white">The Amino Exchange is an educational tool</span> for tracking
            and calculating reconstitution volumes for research peptides.
          </p>
          <p>
            This platform does <span className="text-[#0D9488] font-semibold">not</span> provide medical advice,
            diagnosis, or treatment. Consult a qualified healthcare professional before using any compound.
          </p>
          <ul className="list-none space-y-2 mt-2 border-l-2 border-slate-200 pl-4">
            <li className="flex gap-2"><span className="text-[#0D9488] font-mono-num">01</span><span>You are <strong>18 years or older</strong>.</span></li>
            <li className="flex gap-2"><span className="text-[#0D9488] font-mono-num">02</span><span>Data is stored securely under your private account — never shared.</span></li>
            <li className="flex gap-2"><span className="text-[#0D9488] font-mono-num">03</span><span>All calculations are informational; verify with a professional before dosing.</span></li>
            <li className="flex gap-2"><span className="text-[#0D9488] font-mono-num">04</span><span>You assume full responsibility for your use of this application.</span></li>
          </ul>
        </div>

        {/* Sticky footer — always visible */}
        <div className="p-6 pt-4 border-t border-slate-200 bg-white flex-shrink-0 space-y-3">
          <label
            htmlFor="tos-check"
            className="flex items-start gap-3 p-3 border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300 transition-colors"
          >
            <Checkbox
              id="tos-check"
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#0D9488] data-[state=checked]:border-[#0D9488] data-[state=checked]:text-black rounded-none w-5 h-5"
              data-testid="onboarding-check"
            />
            <span className="text-sm text-slate-800 select-none">I acknowledge and agree to the terms above.</span>
          </label>

          <Button
            disabled={!checked || submitting}
            onClick={accept}
            className="w-full h-12 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="onboarding-accept"
          >
            {submitting ? "Confirming…" : (checked ? "Acknowledge & Continue" : "Check the box to continue")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
